import i18n from '@/i18n';
import { creditCardType } from '../types';
import { supportedCreditCards } from '../constants';

export function validateCc(ccNumb: string): boolean { // v2.0
  const len = ccNumb.length; // The length of the submitted cc number
  // eslint-disable-next-line radix
  let iCCN = parseInt(ccNumb); // integer of ccNumb
  let iTotal = 0; // integer total set at zero
  const bNum = /^\d+$/.test(ccNumb); // by default assume it is a number
  let bResult = false; // by default assume it is NOT a valid cc
  let calc; // used for calculation of each digit

  // if it is NOT a number, you can either alert to the fact, or just pass a failure
  if (!bNum) {
    /* alert("Not a Number"); */bResult = false;
  }

  // Determine if it is the proper length
  if ((len === 0) && (bResult)) { // nothing, field is blank AND passed above # check
    bResult = false;
  } else { // ccNumb is a number and the proper length - let's see if it is a valid card number
    // eslint-disable-next-line no-lonely-if
    if (len >= 15) { // 15 or 16 for AmericanExpress or V/MC
      for (let i = len; i > 0; i -= 1) { // LOOP through the digits of the card
        // eslint-disable-next-line radix
        calc = Math.floor(iCCN % 10); // right most digit
        // eslint-disable-next-line radix
        iTotal += calc; // running total of the card number as we loop - Do Nothing to first digit
        i -= 1; // decrement the count - move to the next digit in the card
        iCCN /= 10; // subtracts right most digit from ccNumb
        // eslint-disable-next-line radix
        // eslint-disable-next-line radix
        calc = Math.floor(iCCN) % 10; // NEXT right most digit
        calc *= 2; // multiply the digit by two
        /* Instead of some screwy method of converting 16 to a
        string and then parsing 1 and 6 and then adding them to make 7, */

        // I use a simple switch statement to change the value of calc2 to 7 if 16 is the multiple.
        switch (calc) {
          case 10: calc = 1; break; // 5*2=10 & 1+0 = 1
          case 12: calc = 3; break; // 6*2=12 & 1+2 = 3
          case 14: calc = 5; break; // 7*2=14 & 1+4 = 5
          case 16: calc = 7; break; // 8*2=16 & 1+6 = 7
          case 18: calc = 9; break; // 9*2=18 & 1+8 = 9
          // eslint-disable-next-line no-self-assign
          default: calc = calc; // 4*2= 8 &   8 = 8  -same for all lower numbers
        }
        iCCN /= 10; // subtracts right most digit from ccNum
        iTotal += calc; // running total of the card number as we loop
      } // END OF LOOP
      if ((iTotal % 10) === 0) { // check to see if the sum Mod 10 is zero
        bResult = true; // This IS (or could be) a valid credit card number.
      } else {
        bResult = false; // This could NOT be a valid credit card number
      }
    }
  }

  return bResult; // Return the results
}

function isValidModulus10(
  ccNumber: string,
  supportedCard: typeof supportedCreditCards[number],
) {
  if (!supportedCard.checkDigit) return true;

  let checksum = 0; // running checksum total
  let j = 1; // takes value of 1 or 2

  // Process each digit one by one starting at the right
  let calc;
  for (let i = ccNumber.length - 1; i >= 0; i -= 1) {
    // Extract the next digit and multiply by 1 or 2 on alternative digits.
    calc = Number(ccNumber.charAt(i)) * j;

    // If the result is in two digits add 1 to the checksum total
    if (calc > 9) {
      checksum += 1;
      calc -= 10;
    }

    // Add the units element to the checksum total
    checksum += calc;

    // Switch the value of j
    if (j === 1) { j = 2; } else { j = 1; }
  }

  // All done - if checksum is divisible by 10, it is a valid modulus 10.
  // If not, report an error.
  if (checksum % 10 !== 0) {
    return false;
  }

  return true;
}

function isPrefixValid(
  ccNumber: string,
  supportedCard: typeof supportedCreditCards[number],
) {
  let isValid = false;

  // Load an array with the valid prefixes for this card
  const prefix = supportedCard.prefixes.split(',');

  // Now see if any of them match what we have in the card number
  for (let i = 0; i < prefix.length; i += 1) {
    const exp = new RegExp(`^${prefix[i]}`);
    if (exp.test(ccNumber)) isValid = true;
  }

  return isValid;
}

function isLengthValid(
  ccNumber: string,
  supportedCard: typeof supportedCreditCards[number],
) {
  let isValid = false;
  const lengths = supportedCard.length.split(',');

  for (let j = 0; j < lengths.length; j += 1) {
    if (ccNumber.length === +lengths[j]) isValid = true;
  }

  return isValid;
}

export function validateCCWithMessage(
  numberString: string,
  cardType?: creditCardType,
): boolean | string {
  if (!cardType) return true;

  const supportedCard = supportedCreditCards.find((cc) => cc.name === cardType);
  if (!supportedCard) {
    return i18n.t('global.validations.creditCard.notSupported') as string;
  }

  const ccNumber = numberString.replace(/\s/g, '');

  const isNumeric = /^[0-9]{13,16}$/.exec(ccNumber);
  if (!isNumeric) {
    return i18n.t('global.validations.creditCard.invalidFormat') as string;
  }

  const isValidDigits = isValidModulus10(ccNumber, supportedCard);
  if (!isValidDigits) {
    return i18n.t('global.validations.creditCard.invalidDigits') as string;
  }

  const isPrefixOk = isPrefixValid(ccNumber, supportedCard);
  if (!isPrefixOk) {
    return i18n.t('global.validations.creditCard.invalidCardNumber') as string;
  }

  const isLengthOk = isLengthValid(ccNumber, supportedCard);
  if (!isLengthOk) {
    return i18n.t('global.validations.creditCard.invalidDigits') as string;
  }

  return true;
}
